import React, { useEffect, useMemo, useRef, useState } from 'react';

import cn from 'classnames';

import { Flex, Icon, UiButton, UiText } from '@shared/ui';

import cls from './Albums.module.css';
import { useAlbums } from './useAlbums';

interface AlbumsProps {
    classes?: string;
}

export const Albums = ({ classes }: AlbumsProps) => {
    const { albums, onSetActiveAlbums, selectedAlbums } = useAlbums();

    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const scrollRef = useRef<HTMLDivElement | null>(null);

    const [showArrows, setShowArrows] = useState(false);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    const updateScrollState = () => {
        const el = scrollRef.current;
        if (!el) return;

        const hasOverflow = el.scrollWidth > el.clientWidth + 1;

        setShowArrows(hasOverflow);
        setCanScrollLeft(el.scrollLeft > 0);
        setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    };

    useEffect(() => {
        updateScrollState();

        const el = scrollRef.current;
        if (!el) return;

        const onScroll = () => updateScrollState();

        el.addEventListener('scroll', onScroll);

        const resizeObserver = new ResizeObserver(() => {
            updateScrollState();
        });

        resizeObserver.observe(el);
        if (wrapperRef.current) {
            resizeObserver.observe(wrapperRef.current);
        }

        Array.from(el.children).forEach((child) => {
            resizeObserver.observe(child);
        });

        return () => {
            el.removeEventListener('scroll', onScroll);
            resizeObserver.disconnect();
        };
    }, [albums]);

    const scrollByHiddenItems = (direction: 'left' | 'right') => {
        const container = scrollRef.current;
        if (!container) return;

        const items = Array.from(container.children) as HTMLElement[];
        if (!items.length) return;

        const containerRect = container.getBoundingClientRect();
        const EPS = 1;

        if (direction === 'right') {
            const hiddenRight = items.filter((item) => {
                const rect = item.getBoundingClientRect();
                return rect.right > containerRect.right + EPS;
            });

            if (!hiddenRight.length) return;

            const targetItem = hiddenRight[Math.min(2, hiddenRight.length - 1)];
            const targetRect = targetItem.getBoundingClientRect();

            const delta = targetRect.right - containerRect.right;
            const nextScrollLeft = Math.min(
                container.scrollLeft + delta,
                container.scrollWidth - container.clientWidth
            );

            container.scrollTo({
                left: nextScrollLeft,
                behavior: 'smooth',
            });

            return;
        }

        const hiddenLeft = items.filter((item) => {
            const rect = item.getBoundingClientRect();
            return rect.left < containerRect.left - EPS;
        });

        if (!hiddenLeft.length) return;

        const targetItem = hiddenLeft[Math.max(hiddenLeft.length - 3, 0)];
        const targetRect = targetItem.getBoundingClientRect();

        const delta = targetRect.left - containerRect.left;
        const nextScrollLeft = Math.max(container.scrollLeft + delta, 0);

        container.scrollTo({
            left: nextScrollLeft,
            behavior: 'smooth',
        });
    };

    return (
        <Flex ref={wrapperRef} className={cn(cls.AlbumsWrapper, [classes])}>
            {showArrows && (
                <UiButton
                    className={cls.arrowButton}
                    variant="icon"
                    startIcon={<Icon iconName="outlineRightArrow" />}
                    onClick={() => scrollByHiddenItems('left')}
                    disabled={!canScrollLeft}
                />
            )}

            <Flex ref={scrollRef} className={cn(cls.Albums)}>
                <div
                    onClick={onSetActiveAlbums()}
                    role="button"
                    className={cn(cls.AlbumItem, !selectedAlbums.length && cls.active)}
                >
                    <UiText>Все</UiText>
                </div>

                {albums.map((album) => (
                    <div
                        onClick={onSetActiveAlbums(album.value)}
                        role="button"
                        className={cn(
                            cls.AlbumItem,
                            selectedAlbums.includes(album.value) && cls.active
                        )}
                        key={album.id}
                    >
                        <UiText>{album.title}</UiText>
                    </div>
                ))}
            </Flex>

            {showArrows && (
                <UiButton
                    className={cls.arrowButton}
                    variant="icon"
                    startIcon={<Icon iconName="outlineLeftArrow" />}
                    onClick={() => scrollByHiddenItems('right')}
                    disabled={!canScrollRight}
                />
            )}
        </Flex>
    );
};
